"use client";

import ErrorIcon from "public/next-public/common/icons/error-icon.svg";
import InfoIcon from "public/next-public/common/icons/info-icon.svg";
import SearchIcon from "public/next-public/common/icons/search-regular.svg";
import XIcon from "public/next-public/common/icons/x-close-icon.svg";
import React, { useRef } from "react";
import styles from "./textInputField.module.scss";

export interface TextInputFieldProps {
  label?: string;
  showLabel?: boolean;
  required?: boolean;
  optional?: boolean;
  placeholder?: string;
  helperText?: string;
  showHelperText?: boolean;
  error?: boolean;
  errorMessage?: React.ReactNode;
  showLeftIcon?: boolean;
  showRightIcon?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  size?: "small" | "medium" | "large";
  disabled?: boolean;
  readOnly?: boolean;
  value?: string;
  defaultValue?: string;
  name?: string;
  id?: string;
  type?: React.HTMLInputTypeAttribute;
  autoComplete?: string;
  className?: string;
  labelClassName?: string;
  pattern?: string;
  maxLength?: number;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  onChange?: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onClear?: () => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onMouseUp?: (event: React.MouseEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  "aria-describedby"?: string;
  "aria-label"?: string;
}

const TextInputField = React.forwardRef<HTMLInputElement, TextInputFieldProps>(function TextInputField(
  {
    label = "Input label",
    showLabel = true,
    required = false,
    optional = false,
    placeholder = "",
    helperText = "",
    showHelperText,
    error = false,
    errorMessage,
    showLeftIcon = true,
    showRightIcon = true,
    leftIcon,
    rightIcon,
    size = "medium",
    disabled = false,
    readOnly = false,
    value,
    defaultValue,
    name,
    id,
    type = "text",
    autoComplete,
    className,
    labelClassName,
    pattern,
    maxLength,
    inputMode,
    onChange,
    onClear,
    onBlur,
    onFocus,
    onKeyDown,
    onMouseUp,
    "aria-describedby": ariaDescribedBy,
    "aria-label": ariaLabel,
  },
  ref,
) {
  const hasHelperText = !!helperText;
  const hasErrorMessage = !!errorMessage;
  const showErrorMessage = error && hasErrorMessage;
  const invalidOnlyHelper = !error && !hasHelperText && hasErrorMessage;
  const resolvedShowHelperText = showHelperText ?? (showErrorMessage || hasHelperText || invalidOnlyHelper);
  const showErrorStyling = error || invalidOnlyHelper;
  const inputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Merge the internal input ref with any forwarded ref so callers that need
  // direct DOM access (e.g. react-number-format's caret management when this
  // component is used as `customInput`) work, while the clear button keeps its
  // own reference to the same node.
  const setInputRef = (node: HTMLInputElement | null): void => {
    inputRef.current = node;
    if (typeof ref === "function") {
      ref(node);
    } else if (ref) {
      ref.current = node;
    }
  };

  const isLarge = size === "large";

  const containerClasses = [styles.TextInputField, styles[size], disabled ? styles.disabled : "", className || ""]
    .filter(Boolean)
    .join(" ");

  const inputAreaClasses = [styles.inputArea, error ? styles.error : "", disabled ? styles.disabled : ""]
    .filter(Boolean)
    .join(" ");

  const handleClear = (): void => {
    if (readOnly) return;
    const element = isLarge ? textareaRef.current : inputRef.current;
    if (element) {
      const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
        isLarge ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype,
        "value",
      )?.set;
      nativeInputValueSetter?.call(element, "");
      element.dispatchEvent(new Event("input", { bubbles: true }));
      element.focus();
    }
    onClear?.();
  };

  const displayHelperText = showErrorStyling && errorMessage ? errorMessage : helperText;

  const helperTextId = id && resolvedShowHelperText ? `${id}-helper` : undefined;
  const mergedAriaDescribedBy = [ariaDescribedBy, helperTextId].filter(Boolean).join(" ") || undefined;

  return (
    <div className={containerClasses}>
      {showLabel && (
        <label className={[styles.label, labelClassName].filter(Boolean).join(" ")} htmlFor={id}>
          {label}
          {required && <span className={styles.required}>*</span>}
          {optional && !required && <span className={styles.optional}>(Optional)</span>}
        </label>
      )}

      <div className={inputAreaClasses} onClick={() => (isLarge ? textareaRef : inputRef).current?.focus()}>
        {showLeftIcon && <span className={styles.icon}>{leftIcon || <SearchIcon width={16} height={16} />}</span>}

        {isLarge ? (
          <textarea
            ref={textareaRef}
            className={styles.textarea}
            placeholder={placeholder}
            value={value}
            defaultValue={defaultValue}
            name={name}
            id={id}
            disabled={disabled}
            readOnly={readOnly}
            required={required}
            aria-invalid={showErrorStyling || undefined}
            aria-label={ariaLabel}
            aria-describedby={mergedAriaDescribedBy}
            maxLength={maxLength}
            autoComplete={autoComplete}
            onChange={onChange}
            onBlur={onBlur}
            onFocus={onFocus}
          />
        ) : (
          <input
            ref={setInputRef}
            className={styles.input}
            type={type}
            placeholder={placeholder}
            value={value}
            defaultValue={defaultValue}
            name={name}
            id={id}
            autoComplete={autoComplete}
            disabled={disabled}
            readOnly={readOnly}
            required={required}
            aria-invalid={showErrorStyling || undefined}
            aria-label={ariaLabel}
            aria-describedby={mergedAriaDescribedBy}
            pattern={pattern}
            maxLength={maxLength}
            inputMode={inputMode}
            onChange={onChange}
            onBlur={onBlur}
            onFocus={onFocus}
            onKeyDown={onKeyDown}
            onMouseUp={onMouseUp}
          />
        )}

        {showRightIcon &&
          (rightIcon ? (
            <span className={styles.icon}>{rightIcon}</span>
          ) : readOnly ? null : (
            <button
              type="button"
              className={styles.clearButton}
              onClick={handleClear}
              disabled={disabled}
              aria-label="Clear input"
              tabIndex={-1}
            >
              <XIcon width={16} height={16} />
            </button>
          ))}
      </div>

      {resolvedShowHelperText && (
        <div
          id={helperTextId}
          role={showErrorStyling && hasErrorMessage ? "alert" : undefined}
          className={[
            styles.helperText,
            showErrorStyling ? styles.errorHelper : "",
            invalidOnlyHelper ? styles.invalidOnly : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          <span className={styles.helperIcon}>
            {showErrorStyling ? <ErrorIcon width={12} height={12} /> : <InfoIcon width={12} height={12} />}
          </span>
          <span className={styles.helperMessage}>{displayHelperText}</span>
        </div>
      )}
    </div>
  );
});

TextInputField.displayName = "TextInputField";

export default TextInputField;
